import type {
  FormattedPriceSettings,
  PurchaseInfo,
  Session,
} from '@setplex/tria-api'
import type { DataEvent, DataEventType } from '~/core/sse'
import type {
  AdjustPurchaseableFn,
  Purchaseable,
  Purchased,
  SsePurchaseInfo,
  SseValue,
} from './index.h'

import {
  PriceSettingsAvailabilityPeriodTimeUnit,
  PriceSettingsPaymentMethod,
  PriceSettingsType,
  PurchaseInfoContentPurchaseType,
} from '@setplex/tria-api'
import { isSseItemId } from './index.h'

export const BASE_PURCHASED: Purchased = {
  channel: new Map(),
  channelSet: new Map(),
  episode: new Map(),
  liveEvent: new Map(),
  partnerProduct: new Map(),
  season: new Map(),
  subscription: new Map(),
  tvShow: new Map(),
  tvShowSet: new Map(),
  vod: new Map(),
  vodSet: new Map(),
}

export const updateMap =
  (purchased: Purchased) =>
  (
    key: keyof Purchased,
    itemId: number,
    purchaseInfo?: SsePurchaseInfo
  ): Purchased => {
    const mapValue = purchased[key].get(itemId)
    if (mapValue) return purchased

    const value: SseValue = purchaseInfo || { itemId }
    purchased[key].set(itemId, value)
    purchased[key] = new Map(purchased[key])
    return { ...purchased }
  }

/**
 * Create `purchaseInfo` for item from its `priceSettings`
 */
export const infoFromPriceSetting = (
  setting: FormattedPriceSettings,
  session: Session | null
): PurchaseInfo => {
  if (setting.type === PriceSettingsType.Purchase) {
    return {
      contentPurchaseType: PurchaseInfoContentPurchaseType.Bought,
    }
  }

  const unit = setting.availabilityPeriodTimeUnit
  const length = setting.availabilityPeriodLength
  if (!unit || !length) {
    return {
      contentPurchaseType: PurchaseInfoContentPurchaseType.Rented,
    }
  }

  const until = new Date()
  if (unit === PriceSettingsAvailabilityPeriodTimeUnit.Day) {
    until.setDate(until.getDate() + length)
  } else if (unit === PriceSettingsAvailabilityPeriodTimeUnit.Month) {
    until.setMonth(until.getMonth() + length)
  } else {
    until.setFullYear(until.getFullYear() + length)
  }

  // adjust `rentedUntil` to current subscription expiration
  if (session && session.expirationTime) {
    const expiration = new Date(session.expirationTime)
    if (until > expiration) {
      until.setTime(expiration.getTime())
    }
  }

  return {
    contentPurchaseType: PurchaseInfoContentPurchaseType.Rented,
    rentedUntil: until.toISOString(),
  }
}

export const isRented = (purchaseInfo: PurchaseInfo) =>
  purchaseInfo.contentPurchaseType === PurchaseInfoContentPurchaseType.Rented

/**
 * Adjust purchaseable item with purchase info
 *
 * `purchaseInfo` is calculated from `priceSettings` of the item,
 * this allows to eliminate extra API calls to get purchase info,
 * but there are could be issues, when one item has multiple price settings,
 * it is impossible to know, which one to use... I use first one
 */
export const adjust =
  (type: keyof Purchased, bundle?: keyof Purchased) =>
  (purchased: Purchased, session: Session | null): AdjustPurchaseableFn =>
  <T extends Purchaseable | undefined>(item: T): T => {
    if (item == null || item.id == null) return item
    if (item.purchaseInfo != null) return item

    const priceSettings = ([] as FormattedPriceSettings[]).concat(
      item.priceSettings || [],
      item.tvShowPriceSettings || [],
      item.seasonPriceSettings || [],
      item.episodePriceSettings || []
    )

    if (!priceSettings.length) return item

    // check if the item is already purchased as per set
    if (bundle && purchased[bundle].size) {
      const purchasedBundles = purchased[bundle]
      for (const setting of priceSettings) {
        const bundle = setting.bundle
        if (
          setting.paymentMethod === PriceSettingsPaymentMethod.PerSet &&
          bundle &&
          bundle.id &&
          purchasedBundles.has(bundle.id)
        ) {
          const value = purchasedBundles.get(bundle.id)

          if (value) {
            const purchaseInfo = isSseItemId(value)
              ? infoFromPriceSetting(setting, session)
              : value

            return {
              ...item,
              rented: isRented(purchaseInfo),
              purchaseInfo,
            }
          }
        }
      }
    }

    // check if the item is already purchased as per item
    const list = purchased[type]
    if (list.size && list.has(item.id)) {
      for (const setting of priceSettings) {
        if (setting.paymentMethod === PriceSettingsPaymentMethod.PerItem) {
          const value = list.get(item.id)

          if (value) {
            const purchaseInfo = isSseItemId(value)
              ? infoFromPriceSetting(setting, session)
              : value

            return {
              ...item,
              rented: isRented(purchaseInfo),
              purchaseInfo,
            }
          }
        }
      }

      // don't know how item was bought...
      // get first price setting and use it to create purchase info
      const setting = priceSettings[0]
      const purchaseInfo = infoFromPriceSetting(setting, session)

      return {
        ...item,
        rented: isRented(purchaseInfo),
        purchaseInfo,
      }
    }

    // item is not purchased
    return item
  }

/**
 * Small helper typescript type guard factory
 */
export function typeOf<T extends DataEvent['data']>(type: DataEventType) {
  return (message: DataEvent['data']): message is T => message.type === type
}
